import {
  DownloadReport,
  ReportPreview,
  CustomQueryDto,
  EyeExaminationAnalytics,
  EyeExaminationList,
  EyeExaminationStage,
  StaffPerformanceRow,
} from '../@types/reporting';
import axios from '../utils/axios';

export class ReportingService {
  // Download report with actual file download
  async downloadReport(data: DownloadReport): Promise<void> {
    try {
      const response = await axios.post('/reports/download', data, {
        responseType: 'blob', // Important for file downloads
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `report.${data.format}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  // Track report download (for analytics)
  trackReportDownload(data: Omit<DownloadReport, 'format'>) {
    return axios.post('/reports/track-download', data);
  }

  // Preview report data
  async previewReport(
    data: Omit<DownloadReport, 'format'>,
  ): Promise<ReportPreview> {
    const response = await axios.post('/reports/preview', data);
    return response.data;
  }

  // Get download history
  getDownloadHistory() {
    return axios.get(`/reports/download-history`);
  }

  // Start asynchronous report generation
  async asyncDownloadReport(data: DownloadReport): Promise<any> {
    const response = await axios.post('/reports/async-download', data);
    return response.data;
  }

  // Check status of a report task
  async getTaskStatus(taskId: number): Promise<any> {
    const response = await axios.get(`/reports/task-status/${taskId}`);
    return response.data;
  }

  // Execute custom query synchronously
  async customQuery(data: CustomQueryDto): Promise<any> {
    const response = await axios.post('/reports/custom-query', data);
    return response.data;
  }

  // Start background custom query
  async asyncCustomQuery(data: CustomQueryDto): Promise<any> {
    const response = await axios.post('/reports/async-custom-query', data);
    return response.data;
  }

  async downloadTaskFile(
    taskId: number,
    fallbackFileName: string,
  ): Promise<void> {
    const response = await axios.get(`/reports/tasks/${taskId}/file`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    const contentDisposition = response.headers['content-disposition'];
    let filename = fallbackFileName;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getDashboardStats(): Promise<any> {
    const response = await axios.get('/reports/dashboard-stats');
    return response.data;
  }

  // Eye examinations completed in the period, split NHS vs private using the
  // exam type recorded at handover.
  async getEyeExaminationAnalytics(params: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<EyeExaminationAnalytics> {
    const response = await axios.get('/reports/sales/eye-examinations', {
      params,
    });
    return response.data;
  }

  // The examinations behind one workflow-stage tile, newest first.
  async getEyeExaminationList(
    params: {
      stage: EyeExaminationStage;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ): Promise<EyeExaminationList> {
    const response = await axios.get('/reports/sales/eye-examinations/list', {
      params,
      signal,
    });
    return response.data;
  }

  // Examination workload per practitioner: completed against outstanding.
  async getStaffPerformance(params: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<StaffPerformanceRow[]> {
    const response = await axios.get('/reports/sales/staff-performance', {
      params,
    });
    return response.data;
  }
}

export const reportingService = new ReportingService();
